class BundlePicker extends HTMLElement {
  static get HIDDEN_CLASS() {
    return "bundle-picker--hidden";
  }

  constructor() {
    super();
    this.boundOnChange = this.onChange.bind(this);
    this.boundOnClick = this.onClick.bind(this);
    this.boundOnVariantChange = this.onVariantChange.bind(this);
    this.boundOnSubmit = this.onFormSubmit.bind(this);
    this.boundOnVariantPickerChange = this.onVariantPickerChange.bind(this);
    this.boundOnVariantInputChange = this.onVariantInputChange.bind(this);
    this.bundleProductCache = new Map();
    this.activeBundleInput = null;
    this.currentBaseVariant = null;
    this._isInitialized = false;
    this.refreshController = null;
    this.lastSyncedBaseVariantId = null;
  }

  connectedCallback() {
    if (this._isInitialized) return;
    this._isInitialized = true;

    this.sectionId = this.dataset.section;
    this.form = document.getElementById(`product-form-${this.sectionId}`);
    this.variantInput = this.form?.querySelector('input[name="id"]');
    this.priceRoot = document.getElementById(`price-${this.sectionId}`);
    this.variantPicker = document.querySelector(
      `variant-radios[data-section="${this.sectionId}"], variant-selects[data-section="${this.sectionId}"]`
    );
    this.productUrl =
      this.dataset.productUrl ||
      this.variantPicker?.dataset.url ||
      window.location.pathname;
    this.currentBaseVariantId =
      this.dataset.currentVariantId || this.variantInput?.value || null;

    this.hydrateBaseVariant();
    this.bindEvents();
  }

  disconnectedCallback() {
    this.removeEventListener("change", this.boundOnChange);
    this.removeEventListener("click", this.boundOnClick);
    document.removeEventListener("variant:change", this.boundOnVariantChange);
    this.form?.removeEventListener("submit", this.boundOnSubmit);
    this.variantPicker?.removeEventListener("change", this.boundOnVariantPickerChange);
    this.variantInput?.removeEventListener("change", this.boundOnVariantInputChange);
    if (this.refreshController) this.refreshController.abort();
    this._isInitialized = false;
  }

  bindEvents() {
    this.addEventListener("change", this.boundOnChange);
    this.addEventListener("click", this.boundOnClick);
    document.addEventListener("variant:change", this.boundOnVariantChange);
    this.form?.addEventListener("submit", this.boundOnSubmit);
    this.variantPicker?.addEventListener("change", this.boundOnVariantPickerChange);
    this.variantInput?.addEventListener("change", this.boundOnVariantInputChange);
  }

  hydrateBaseVariant() {
    const variantData = this.getVariantData();
    if (!variantData?.length) return;

    const activeId = Number(this.currentBaseVariantId);
    const fallbackId = Number(this.variantInput?.value);
    this.currentBaseVariant =
      variantData.find((variant) => variant.id === activeId) ||
      variantData.find((variant) => variant.id === fallbackId) ||
      null;
  }

  getVariantData() {
    const host = document.querySelector(
      `variant-radios[data-section="${this.sectionId}"], variant-selects[data-section="${this.sectionId}"]`
    );
    let jsonNode = host?.querySelector('script[type="application/json"]');

    // Homepage buybox uses custom option UI, so variant JSON lives on the local card wrapper.
    if (!jsonNode) {
      jsonNode = this.closest("product-buybox-card")?.querySelector("[data-variants]");
    }

    if (!jsonNode) return [];

    try {
      return JSON.parse(jsonNode.textContent);
    } catch (error) {
      console.error("BundlePicker: failed to parse variant JSON", error);
      return [];
    }
  }

  onClick(event) {
    const input = event.target.closest('input.bundle-option[type="radio"]');
    if (!input) return;

    // Native radios cannot be unchecked. Clicking the same active bundle toggles it off.
    if (this.activeBundleInput && this.activeBundleInput === input) {
      event.preventDefault();
      input.checked = false;
      this.activeBundleInput = null;
      this.applyBaseVariantState();
    }
  }

  async onChange(event) {
    const input = event.target.closest('input.bundle-option[type="radio"]');
    if (!input) return;
    if (!this.variantInput) return;

    this.activeBundleInput = input.checked ? input : null;

    if (!input.checked) {
      this.applyBaseVariantState();
      return;
    }

    this.variantInput.value = input.dataset.bundleVariantId || input.value;
    this.variantInput.dispatchEvent(new Event("change", { bubbles: true }));
    await this.applyBundlePrice(input);
  }

  onVariantChange(event) {
    if (!event.detail || event.detail.sectionId !== this.sectionId) return;

    this.activeBundleInput = null;
    this.querySelectorAll('input.bundle-option[type="radio"]').forEach((input) => {
      input.checked = false;
    });

    this.currentBaseVariant = event.detail.variant || null;
    this.currentBaseVariantId = this.currentBaseVariant
      ? String(this.currentBaseVariant.id)
      : this.currentBaseVariantId;
    this.dataset.currentVariantId = this.currentBaseVariantId || "";

    const sourceSectionId =
      event.detail.formElement?.dataset?.originalSection || this.sectionId;
    this.syncBundleMarkup(this.currentBaseVariantId, sourceSectionId);
    this.applyBaseVariantState();
  }

  onVariantPickerChange() {
    // Backup path in case theme scripts don't emit variant:change for any reason.
    window.requestAnimationFrame(() => {
      if (this.activeBundleInput?.checked) return;
      const baseVariantId = this.variantInput?.value;
      if (!baseVariantId) return;

      this.currentBaseVariantId = String(baseVariantId);
      this.dataset.currentVariantId = this.currentBaseVariantId;
      this.hydrateBaseVariant();
      this.syncBundleMarkup(this.currentBaseVariantId, this.sectionId);
    });
  }

  onVariantInputChange() {
    if (this.activeBundleInput?.checked) return;
    if (!this.variantInput?.value) return;
    this.currentBaseVariantId = String(this.variantInput.value);
    this.dataset.currentVariantId = this.currentBaseVariantId;
    this.hydrateBaseVariant();
  }

  onFormSubmit() {
    if (!this.variantInput) return;

    // Ensure add-to-cart always uses the active bundle variant when selected.
    if (this.activeBundleInput?.checked) {
      this.variantInput.value =
        this.activeBundleInput.dataset.bundleVariantId ||
        this.activeBundleInput.value;
    } else if (this.currentBaseVariant?.id) {
      this.variantInput.value = this.currentBaseVariant.id;
    }
  }

  async applyBundlePrice(input) {
    const productHandle = input.dataset.bundleProductHandle;
    const variantId = Number(input.dataset.bundleVariantId || input.value);
    if (!productHandle || !variantId) return;

    const productData = await this.getBundleProductData(productHandle);
    if (!productData) return;

    const bundleVariant =
      productData.variants.find((variant) => variant.id === variantId) ||
      productData.variants[0];
    if (!bundleVariant) return;

    this.renderPrice(bundleVariant.price, bundleVariant.compare_at_price);
  }

  applyBaseVariantState() {
    if (!this.variantInput) return;

    const fallbackVariantId =
      this.currentBaseVariantId || this.dataset.currentVariantId || null;
    if (!fallbackVariantId) return;

    this.variantInput.value = fallbackVariantId;
    this.variantInput.dispatchEvent(new Event("change", { bubbles: true }));

    const baseVariant = this.currentBaseVariant;
    if (!baseVariant) return;
    this.renderPrice(baseVariant.price, baseVariant.compare_at_price);
  }

  async getBundleProductData(productHandle) {
    if (this.bundleProductCache.has(productHandle)) {
      return this.bundleProductCache.get(productHandle);
    }

    try {
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const productData = await response.json();
      this.bundleProductCache.set(productHandle, productData);
      return productData;
    } catch (error) {
      console.error("BundlePicker: failed to load bundle product", error);
      return null;
    }
  }

  renderPrice(priceCents, compareAtPriceCents) {
    if (!this.priceRoot) return;

    const isOnSale =
      Number(compareAtPriceCents) > 0 &&
      Number(compareAtPriceCents) > Number(priceCents);
    const moneyPrice = formatMoney(priceCents);
    const moneyComparePrice = isOnSale ? formatMoney(compareAtPriceCents) : "";

    this.priceRoot
      .querySelectorAll(".price-item--sale, .price__regular .price-item--regular")
      .forEach((node) => {
        node.textContent = moneyPrice;
      });

    const compareNode = this.priceRoot.querySelector(
      ".price__compare .price-item--regular"
    );
    if (compareNode) compareNode.textContent = moneyComparePrice;

    this.priceRoot.classList.toggle("price--on-sale", isOnSale);
  }

  replaceBundleMarkupForVariant(variantId, sourceSectionId = this.sectionId) {
    if (!variantId || !this.sectionId) return;

    if (this.refreshController) this.refreshController.abort();
    this.refreshController = new AbortController();

    const productUrl = `${this.productUrl}?variant=${variantId}&section_id=${sourceSectionId}`;
    fetch(productUrl, { signal: this.refreshController.signal })
      .then((response) => response.text())
      .then((htmlString) => {
        const html = new DOMParser().parseFromString(htmlString, "text/html");
        const source =
          html.getElementById(`bundle-picker-${sourceSectionId}`) ||
          html.getElementById(`bundle-picker-${this.sectionId}`);
        if (!source) {
          this.classList.add(BundlePicker.HIDDEN_CLASS);
          window.setTimeout(() => {
            if (this.classList.contains(BundlePicker.HIDDEN_CLASS)) {
              this.innerHTML = "";
            }
          }, 220);
          this.activeBundleInput = null;
          return;
        }

        this.classList.remove(BundlePicker.HIDDEN_CLASS);
        this.innerHTML = source.innerHTML;
        this.activeBundleInput = null;
      })
      .catch((error) => {
        if (error?.name === "AbortError") return;
        console.error("BundlePicker: failed to refresh bundle options", error);
      });
  }

  syncBundleMarkup(variantId, sourceSectionId = this.sectionId) {
    const normalizedVariantId = String(variantId || "");
    if (!normalizedVariantId) return;
    if (this.lastSyncedBaseVariantId === normalizedVariantId) return;

    this.lastSyncedBaseVariantId = normalizedVariantId;
    this.replaceBundleMarkupForVariant(normalizedVariantId, sourceSectionId);
  }
}

if (!customElements.get("bundle-picker")) {
  customElements.define("bundle-picker", BundlePicker);
}
